<template>
  <v-card>
    <v-stepper v-model="currentStep">
      <tsp-update-stepper-header :current-step="currentStep" />

      <v-stepper-window>
        <select-profiles-step
          v-if="currentStep === '1'"
          :profiles="allTaskSetProfiles"
          :loading="loading"
          @continue="currentStep = '2'"
          @export="handleExport"
        />

        <upload-tsp-step
          v-if="currentStep === '2'"
          :existing-profiles="allTaskSetProfiles"
          @update:import-result="handleImportResult"
          @back="currentStep = '1'"
          @continue="currentStep = '3'"
        />

        <review-tsp-changes-step
          v-if="currentStep === '3'"
          :import-result="importResult"
          :existing-profiles="allTaskSetProfiles"
          :teams="teams"
          :updating="updating"
          @back="currentStep = '2'"
          @confirm="handleConfirmUpdate"
        />
      </v-stepper-window>
    </v-stepper>

    <!-- Success Snackbar -->
    <v-snackbar v-model="showSuccessSnackbar" :timeout="3000" color="success">
      {{ successMessage }}
    </v-snackbar>

    <!-- Error Snackbar -->
    <v-snackbar v-model="showErrorSnackbar" :timeout="5000" color="error">
      {{ errorMessage }}
    </v-snackbar>
  </v-card>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useAppStore } from '@/stores/app';
import { useSettingsStore } from '@/stores/settings';
import TaskSetProfileToSpreadsheetConverter from '@/utils/TaskSetProfileToSpreadsheetConverter';
import TspUpdateStepperHeader from '@components/steps/tsp-update/TspUpdateStepperHeader.vue';
import SelectProfilesStep from '@components/steps/tsp-update/SelectProfilesStep.vue';
import UploadTspStep from '@components/steps/tsp-update/UploadTspStep.vue';
import ReviewTspChangesStep from '@components/steps/tsp-update/ReviewTspChangesStep.vue';

const appStore = useAppStore();
const settingsStore = useSettingsStore();

// Stepper state
const currentStep = ref('1');

// Data state
const loading = ref(false);
const updating = ref(false);
const allTaskSetProfiles = ref([]);
const importResult = ref(null);
const teams = ref([]);

// Snackbar state
const showSuccessSnackbar = ref(false);
const successMessage = ref('');
const showErrorSnackbar = ref(false);
const errorMessage = ref('');

// Computed
const projectCode = computed(() => settingsStore.getProjectCode);

// Load profiles and teams on mount
async function loadProfiles() {
  loading.value = true;
  try {
    const [profiles, teamsData] = await Promise.all([
      appStore.apiClient.get(`/api/${projectCode.value}/task-set-profiles`),
      appStore.apiClient.get(`/api/${projectCode.value}/teams`)
    ]);
    // Store all profiles (not just published) for management, sorted by title
    allTaskSetProfiles.value = profiles
      .map(p => ({
        title: `${p.code} | ${p.title}`,
        code: p.code,
        profile: p
      }))
      .sort((a, b) => (a.profile.title || '').localeCompare(b.profile.title || ''));
    teams.value = teamsData || [];
  } catch (error) {
    console.error('Error loading profiles:', error);
    errorMessage.value = 'Failed to load profiles';
    showErrorSnackbar.value = true;
  } finally {
    loading.value = false;
  }
}

// Handle export from SelectProfilesStep
async function handleExport({ profiles, options }) {
  try {
    // Fetch reference data
    const [teams, issueTemplates, processTemplates] = await Promise.all([
      appStore.apiClient.get(`/api/${projectCode.value}/teams`),
      appStore.apiClient.get('/api/issue-templates?published=true'),
      appStore.apiClient.get('/api/process-templates?published=true')
    ]);

    const templates = [
      ...issueTemplates.map(t => ({ ...t, type: 'issue' })),
      ...processTemplates.map(t => ({ ...t, type: 'process' }))
    ];

    const converter = new TaskSetProfileToSpreadsheetConverter();
    const buffer = await converter.process(profiles, teams, templates, options);

    // Download file
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `task-set-profiles-${new Date().toISOString().split('T')[0]}.xlsx`;
    link.click();
    URL.revokeObjectURL(url);

    successMessage.value = `Exported ${profiles.length} profile(s)`;
    showSuccessSnackbar.value = true;
  } catch (error) {
    console.error('Error exporting profiles:', error);
    errorMessage.value = 'Failed to export profiles';
    showErrorSnackbar.value = true;
  }
}

// Handle import result from UploadTspStep
function handleImportResult(result) {
  importResult.value = result;
}

// Handle confirm update from ReviewTspChangesStep
async function handleConfirmUpdate(profilesToUpdate) {
  if (!profilesToUpdate || profilesToUpdate.length === 0) return;

  updating.value = true;
  let updatedCount = 0;
  const errors = [];

  try {
    for (const profile of profilesToUpdate) {
      const existing = allTaskSetProfiles.value.find(tsp => tsp.code === profile.code);

      if (existing) {
        try {
          // Preserve API-managed fields from existing profile
          const updatePayload = {
            ...profile,
            id: existing.profile.id,
            key: existing.profile.key,
            isPublished: existing.profile.isPublished,
            publishMessage: existing.profile.publishMessage
          };

          await appStore.apiClient.put(
            `/api/${projectCode.value}/task-set-profiles/${existing.profile.id}`,
            updatePayload
          );
          updatedCount++;
        } catch (error) {
          console.error(`Error updating profile ${profile.code}:`, error);
          errors.push(`${profile.code}: ${error.message || 'Update failed'}`);
        }
      }
    }

    await loadProfiles();

    // Reset state and go back to step 1
    importResult.value = null;
    currentStep.value = '1';

    if (errors.length > 0) {
      errorMessage.value = `Updated ${updatedCount} profile(s). Errors: ${errors.join(', ')}`;
      showErrorSnackbar.value = true;
    } else {
      successMessage.value = `Successfully updated ${updatedCount} profile(s)`;
      showSuccessSnackbar.value = true;
    }
  } catch (error) {
    console.error('Error during import:', error);
    errorMessage.value = 'Failed to complete import';
    showErrorSnackbar.value = true;
  } finally {
    updating.value = false;
  }
}

onMounted(loadProfiles);
</script>
